import React, { useState } from "react";
import { Pencil, Download, Eye } from "lucide-react";
import ViewModal from "./ViewModal";
import { downloadFormDocx } from "./Downloadformdocx";

const TH = ({ children }) => (
  <th style={{ padding: "14px 16px", textAlign: "left", color: "#999", fontSize: "11px", fontWeight: "700", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
    {children}
  </th>
);

const TD = ({ children, style = {} }) => (
  <td style={{ padding: "14px 16px", ...style }}>{children}</td>
);

const Skeleton = ({ width = "100%", height = "13px", radius = "6px" }) => (
  <span style={{ display: "inline-block", width, height, borderRadius: radius, background: "linear-gradient(90deg, #f0f0f0 25%, #e4e4e4 50%, #f0f0f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
);

const SkeletonRow = ({ idx }) => (
  <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
    <TD><Skeleton width="20px" /></TD>
    <TD><Skeleton width={idx % 2 === 0 ? "130px" : "100px"} /></TD>
    <TD><Skeleton width="90px" /></TD>
    <TD><Skeleton width={idx % 3 === 0 ? "160px" : "120px"} /></TD>
    <TD><Skeleton width="72px" height="22px" radius="20px" /></TD>
    <TD>
      <div style={{ display: "flex", gap: "10px" }}>
        <Skeleton width="26px" height="26px" radius="6px" />
        <Skeleton width="26px" height="26px" radius="6px" />
        <Skeleton width="26px" height="26px" radius="6px" />
      </div>
    </TD>
  </tr>
);

const ActionBtn = ({ title, onClick, disabled, iconColor, hoverBg, children }) => (
  <button
    onClick={onClick}
    title={title}
    disabled={disabled}
    style={{
      background: "transparent",
      border: "none",
      padding: "7px",
      borderRadius: "8px",
      cursor: disabled ? "not-allowed" : "pointer",
      color: disabled ? "#CBD5E1" : iconColor,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.15s ease",
      opacity: disabled ? 0.45 : 1,
    }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = hoverBg; }}
    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
  >
    {children}
  </button>
);

const AssessmentTable = ({ records, onEdit, loading = false }) => {
  const [viewRecord, setViewRecord] = useState(null);

  return (
    <>
      <div style={{ background: "white", borderRadius: "12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ background: "#fafafa", borderBottom: "2px solid #e5e7eb" }}>
              <TH>S.NO</TH>
              <TH>TOOL NAME</TH>
              <TH>TYPE</TH>
              <TH>SUBMITTED BY</TH>
              <TH>STATUS</TH>
              <TH>ACTIONS</TH>
            </tr>
          </thead>
          <tbody>

            {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} idx={i} />)}

            {!loading && records.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: "52px", textAlign: "center", color: "#bbb", fontSize: "14px" }}>
                  No records found. Click <strong>+ New Form</strong> to get started.
                </td>
              </tr>
            )}

            {!loading && records.map((record, idx) => {
              const isDraft = record.status?.toLowerCase() === "draft";
              return (
                <tr
                  key={record.id}
                  style={{ borderBottom: "1px solid #f3f4f6", transition: "background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                  onMouseLeave={e => e.currentTarget.style.background = "white"}
                >
                  <TD style={{ color: "#666" }}>{idx + 1}</TD>
                  <TD style={{ color: "#111" }}>{record.tool}</TD>
                  <TD style={{ color: "#555" }}>{record.type}</TD>
                  <TD style={{ color: "#555" }}>{record.submittedBy}</TD>
                  <TD>
                    <span style={{
                      padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "600",
                      background: isDraft ? "#fef3c7" : "#f3e8ff",
                      color:      isDraft ? "#92400e" : "#bb1ccc",
                    }}>
                      {record.status?.charAt(0).toUpperCase() + record.status?.slice(1)}
                    </span>
                  </TD>
                  <TD>
                    <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>

                      <ActionBtn
                        title={isDraft ? "Edit Draft" : "Cannot edit a submitted form"}
                        onClick={() => isDraft && onEdit(record)}
                        disabled={!isDraft}
                        iconColor="#0000a0"
                        hoverBg="#EFF6FF"
                      >
                        <Pencil size={15} />
                      </ActionBtn>

                      <ActionBtn
                        title={isDraft ? "Only submitted forms can be downloaded" : "Download Word Report"}
                        onClick={() => !isDraft && downloadFormDocx(record)}
                        disabled={isDraft}
                        iconColor="#16A34A"
                        hoverBg="#F0FDF4"
                      >
                        <Download size={15} />
                      </ActionBtn>

                      <ActionBtn
                        title="View Form"
                        onClick={() => setViewRecord(record)}
                        iconColor="#2563EB"
hoverBg="#EFF6FF"
                      >
                        <Eye size={15} />
                      </ActionBtn>

                    </div>
                  </TD>
                </tr>
              );
            })}

          </tbody>
        </table>
      </div>

      {viewRecord && <ViewModal record={viewRecord} onClose={() => setViewRecord(null)} />}
    </>
  );
};

export default AssessmentTable;